import React from "react";
import { Box, Stepper, Step, StepLabel, useMediaQuery } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { dt } from "../../../shared/themes/redesignTokens";

interface WizardStepperProps {
  steps: string[];
  activeStep: number;
  onStepClick?: (step: number) => void;
}

const WizardStepper = ({ steps, activeStep, onStepClick }: WizardStepperProps) => {
  const isMobile = useMediaQuery("(max-width:768px)");

  if (isMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontFamily: dt.font,
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${dt.blue} 0%, #2E86C1 100%)`,
            color: "#fff",
            borderRadius: dt.radiusPill,
            px: 1.5,
            py: 0.25,
            fontSize: "12px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            boxShadow: "0 2px 6px rgba(52,152,219,0.2)",
          }}
        >
          {activeStep + 1}/{steps.length}
        </Box>
        <Box sx={{ fontSize: "13px", fontWeight: 600, color: dt.textPrimary }}>
          {steps[activeStep]}
        </Box>
      </Box>
    );
  }

  return (
    <Stepper
      activeStep={activeStep}
      sx={{
        "& .MuiStepLabel-label": {
          fontFamily: dt.font,
          fontSize: "13px",
          fontWeight: 500,
          color: dt.textMuted,
          transition: "all 0.25s ease",
          "&.Mui-active": { color: dt.textPrimary, fontWeight: 700 },
          "&.Mui-completed": { color: dt.textSecondary, fontWeight: 500 },
        },
        "& .MuiStepIcon-root": {
          color: dt.textMuted,
          transition: "all 0.3s ease",
          "&.Mui-active": {
            color: dt.blue,
            filter: "drop-shadow(0 2px 4px rgba(52,152,219,0.3))",
          },
          "&.Mui-completed": { color: dt.blue },
        },
        "& .MuiStepConnector-line": {
          borderColor: "#E5E7EB",
          transition: "border-color 0.3s ease",
        },
        "& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
          borderColor: dt.blue,
        },
        "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line": {
          borderColor: dt.blue,
        },
      }}
    >
      {steps.map((label, index) => (
        <Step
          key={index}
          completed={index < activeStep}
          sx={{
            cursor: index < activeStep && onStepClick ? "pointer" : "default",
            "&:hover": index < activeStep && onStepClick ? { opacity: 0.8 } : {},
            transition: "opacity 0.2s ease",
          }}
          onClick={() => {
            if (index < activeStep && onStepClick) {
              onStepClick(index);
            }
          }}
        >
          <StepLabel
            StepIconComponent={
              index < activeStep
                ? () => (
                    <CheckCircleIcon
                      sx={{
                        color: dt.blue,
                        fontSize: "24px",
                        animation: "fadeUp 0.3s cubic-bezier(0.4,0,0.2,1) both",
                      }}
                    />
                  )
                : undefined
            }
          >
            {label}
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
};

export default WizardStepper;
